import React, { useState, useEffect } from 'react';
import { getConsentGraphics, getClinicalTerms, getCieCodes } from '../../servicios/apiClinica';
import TermAutocompleteTextArea from '../../comun/TermAutocompleteTextArea';
import TermAutocomplete from '../../comun/TermAutocomplete';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Fase12 = ({ isViewOnly, formData, setFormData }) => {
    const treatments = formData.treatments || Array.from({ length: 9 }, () => ({}));
    const [treatmentsCatalog, setTreatmentsCatalog] = useState([]);
    const [serviciosCatalog, setServiciosCatalog] = useState([]);
    const [consentGraphicsList, setConsentGraphicsList] = useState([]);
    const [showAddMenu, setShowAddMenu] = useState(false);
    
    // Terminos clinicos - un solo listado unificado
    const [allTerms, setAllTerms] = useState([]);
    const [cieCodes, setCieCodes] = useState([]);

    useEffect(() => {

        const fetchTreatmentsCatalog = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const response = await fetch(`${API_URL}/api/clinical-records/procedures/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) setTreatmentsCatalog(data);
                    else if (data && Array.isArray(data.results)) setTreatmentsCatalog(data.results);
                }
            } catch (error) {
                console.error(error);
            }
        };

        const fetchServiciosCatalog = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const response = await fetch(`${API_URL}/api/treatments/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) setServiciosCatalog(data);
                    else if (data && Array.isArray(data.results)) setServiciosCatalog(data.results);
                }
            } catch (error) {
                console.error(error);
            }
        };

        const fetchConsentGraphics = async () => {
            try {
                const data = await getConsentGraphics();
                setConsentGraphicsList(data || []);
            } catch (error) {
                console.error(error);
            }
        };
        

        const fetchClinicalTerms = async () => {
            try {
                const [terms, cie] = await Promise.all([
                    getClinicalTerms(),  // Sin categoría = trae todos
                    getCieCodes()
                ]);
                setAllTerms(terms || []);
                setCieCodes(cie || []);
            } catch (error) {
                console.error(error);
            }
        };

        fetchTreatmentsCatalog();
        fetchServiciosCatalog();
        fetchConsentGraphics();
        fetchClinicalTerms();
    }, []);

    const updateTreatment = (index, field, value) => {
        setFormData(prev => {
            const newTreatments = [...(prev.treatments || [])];
            if (!newTreatments[index]) newTreatments[index] = {};
            newTreatments[index] = { ...newTreatments[index], [field]: value };
            return {
                ...prev,
                treatments: newTreatments
            };
        });
    };

    const updateConsent = (index, field, value) => {
        setFormData(prev => {
            const currentConsents = Array.isArray(prev.consent) ? [...prev.consent] : [prev.consent || {}];
            if (!currentConsents[index]) currentConsents[index] = {};
            currentConsents[index] = { ...currentConsents[index], [field]: value };
            return {
                ...prev,
                consent: currentConsents
            };
        });
    };

    const handleAddConsent = (diag = null) => {
        setFormData(prev => {
            const currentConsents = Array.isArray(prev.consent) ? [...prev.consent] : [prev.consent || {}];
            const newConsent = { ambulatorio: true };
            
            if (currentConsents.length > 0) {
                const first = currentConsents[0];
                newConsent.primer_nombre = first.primer_nombre;
                newConsent.segundo_nombre = first.segundo_nombre;
                newConsent.primer_apellido = first.primer_apellido;
                newConsent.segundo_apellido = first.segundo_apellido;
                newConsent.historia_clinica = first.historia_clinica;
                newConsent.consentimiento_para = first.consentimiento_para;
                newConsent.sexo = first.sexo;
                newConsent.edad = first.edad;
                newConsent.servicio = first.servicio || 'ODONTOLOGÍA GENERAL';
            }
            
            if (diag) {
                newConsent.diagnostico = diag.description;
                newConsent.cie10 = diag.cie;
            }
            
            return { ...prev, consent: [...currentConsents, newConsent] };
        });
        setShowAddMenu(false);
    };

    const handleAddAllConsents = () => {
        const diagnoses = (formData.diagnoses || []).filter(d => d.description || d.cie);
        const currentConsents = Array.isArray(formData.consent) ? formData.consent : [formData.consent || {}];
        
        const existingCies = currentConsents.map(c => c.cie10).filter(Boolean);
        const missingDiagnoses = diagnoses.filter(d => d.cie && !existingCies.includes(d.cie));
        
        if (missingDiagnoses.length === 0) {
            alert("Ya existen consentimientos para todos los diagnósticos seleccionados.");
            setShowAddMenu(false);
            return;
        }
        
        setFormData(prev => {
            let newConsents = Array.isArray(prev.consent) ? [...prev.consent] : [prev.consent || {}];
            missingDiagnoses.forEach(diag => {
                const newConsent = { ambulatorio: true };
                if (newConsents.length > 0) {
                    const first = newConsents[0];
                    newConsent.primer_nombre = first.primer_nombre;
                    newConsent.segundo_nombre = first.segundo_nombre;
                    newConsent.primer_apellido = first.primer_apellido;
                    newConsent.segundo_apellido = first.segundo_apellido;
                    newConsent.historia_clinica = first.historia_clinica;
                    newConsent.consentimiento_para = first.consentimiento_para;
                    newConsent.sexo = first.sexo;
                    newConsent.edad = first.edad;
                    newConsent.servicio = first.servicio || 'ODONTOLOGÍA GENERAL';
                }
                newConsent.diagnostico = diag.description;
                newConsent.cie10 = diag.cie;
                newConsents.push(newConsent);
            });
            return { ...prev, consent: newConsents };
        });
        setShowAddMenu(false);
    };

    const handleDeleteConsent = (index) => {
        if (window.confirm('¿Está seguro de eliminar este consentimiento?')) {
            setFormData(prev => {
                const currentConsents = Array.isArray(prev.consent) ? [...prev.consent] : [prev.consent || {}];
                currentConsents.splice(index, 1);
                return { ...prev, consent: currentConsents };
            });
        }
    };

    const consents = Array.isArray(formData.consent) ? formData.consent : [formData.consent || {}];

    // Auto-llenar datos del paciente en los consentimientos
    useEffect(() => {
        if (!formData.first_name && !formData.last_name) return;
        
        const currentConsents = Array.isArray(formData.consent) ? formData.consent : [formData.consent || {}];
        let changed = false;
        
        const newConsents = currentConsents.map((consent, idx) => {
            const needsUpdate = {};
            
            const nombres = (formData.first_name || '').trim().split(/\s+/);
            const apellidos = (formData.last_name || '').trim().split(/\s+/);
            
            const pNombre = nombres[0] || '';
            const sNombre = nombres.slice(1).join(' ') || '';
            const pApellido = apellidos[0] || '';
            const sApellido = apellidos.slice(1).join(' ') || '';

            if (consent.primer_nombre !== pNombre) needsUpdate.primer_nombre = pNombre;
            if (consent.segundo_nombre !== sNombre) needsUpdate.segundo_nombre = sNombre;
            if (consent.primer_apellido !== pApellido) needsUpdate.primer_apellido = pApellido;
            if (consent.segundo_apellido !== sApellido) needsUpdate.segundo_apellido = sApellido;
            
            if (!consent.historia_clinica && formData.cedula) needsUpdate.historia_clinica = formData.cedula;
            
            const fullName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
            if (consent.consentimiento_para !== fullName) needsUpdate.consentimiento_para = fullName;
            
            if (formData.gender && consent.sexo !== formData.gender) needsUpdate.sexo = formData.gender;
            
            if (formData.birth_date) {
                const parts = formData.birth_date.split('-');
                if (parts.length === 3) {
                    const birthYear = parseInt(parts[0], 10);
                    const birthMonth = parseInt(parts[1], 10) - 1;
                    const birthDay = parseInt(parts[2], 10);
                    const birth = new Date(birthYear, birthMonth, birthDay);
                    const today = new Date();
                    let age = today.getFullYear() - birth.getFullYear();
                    const m = today.getMonth() - birth.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                    
                    if (age >= 0 && consent.edad !== String(age)) {
                        needsUpdate.edad = String(age);
                        needsUpdate.condicion_a = 'X';
                    }
                }
            }
            
            if (idx === 0) {
                const diagnoses = formData.diagnoses || [];
                const firstDiag = diagnoses.find(d => d.description || d.cie);
                if (firstDiag) {
                    if (!consent.diagnostico && firstDiag.description) needsUpdate.diagnostico = firstDiag.description;
                    if (!consent.cie10 && firstDiag.cie) needsUpdate.cie10 = firstDiag.cie;
                }
            }

            if (!consent.servicio) needsUpdate.servicio = 'ODONTOLOGÍA GENERAL';
            
            if (Object.keys(needsUpdate).length > 0) {
                changed = true;
                return { ...consent, ...needsUpdate };
            }
            return consent;
        });
        
        if (changed) {
            setFormData(prev => ({ ...prev, consent: newConsents }));
        }
    }, [formData.first_name, formData.last_name, formData.cedula, formData.birth_date, formData.diagnoses]);

    const cedulaCompleta = formData.cedula || '';


    const HeaderStyle = { backgroundColor: '#b5d7a8', color: '#000', fontWeight: 'bold', padding: '4px 8px', borderBottom: '1px solid #000' };
    const SubHeaderStyle = { backgroundColor: '#d9ead3', color: '#000', fontSize: '9px', padding: '4px 6px', textAlign: 'center', fontWeight: 'bold', verticalAlign: 'middle', borderRight: '1px solid #000', borderBottom: '1px solid #000' };
    const InputStyle = { width: '100%', border: 'none', outline: 'none', backgroundColor: 'transparent', height: '100%', fontFamily: 'Arial, sans-serif', padding: '4px', resize: 'none', wordWrap: 'break-word', wordBreak: 'break-all', whiteSpace: 'pre-wrap' };
    const TableStyle = { width: '100%', border: '1.5px solid #000', borderCollapse: 'collapse', marginBottom: '0', fontFamily: 'Arial, sans-serif', fontSize: '12px', tableLayout: 'fixed' };
    const TdStyle = { borderBottom: '1px solid #000', borderRight: '1px solid #000', padding: '0', wordWrap: 'break-word', wordBreak: 'break-all' };
    const InnerLabelStyle = { backgroundColor: '#d1e0e0', fontSize: '9px', fontWeight: 'bold', padding: '2px 4px' };

    return (
        <div style={{ width: '100%', marginBottom: '0' }}>
            <table style={{ ...TableStyle, borderTop: 'none' }}>
                <tbody>
                    <tr>
                        <td colSpan="5" style={{ ...HeaderStyle }}>
                            <span style={{ fontSize: '16px', marginRight: '10px' }}>12</span>
                            <span style={{ fontSize: '14px' }}>TRATAMIENTO</span>
                        </td>
                    </tr>
                    <tr>
                        <td style={{ ...SubHeaderStyle, width: '12%' }}>SESIÓN Y FECHA</td>
                        <td style={{ ...SubHeaderStyle, width: '26%' }}>DIAGNÓSTICOS Y COMPLICACIONES</td>
                        <td style={{ ...SubHeaderStyle, width: '26%' }}>PROCEDIMIENTOS</td>
                        <td style={{ ...SubHeaderStyle, width: '24%' }}>PRESCRIPCIONES</td>
                        <td style={{ ...SubHeaderStyle, width: '12%' }}>CÓDIGO</td>
                    </tr>
                    {treatments.map((treatment, idx) => (
                        <tr key={`treatment-${idx}`}>
                            <td style={{ ...TdStyle, verticalAlign: 'top' }}>
                                <table style={{ width: '100%', height: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ ...InnerLabelStyle, width: '50%', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>SESIÓN</td>
                                            <td style={{ width: '50%', textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                                                {idx + 1}
                                                {!isViewOnly && (
                                                    <span 
                                                        className="d-print-none"
                                                        onClick={() => {
                                                            if (window.confirm('¿Está seguro de eliminar esta sesión?')) {
                                                                setFormData(prev => {
                                                                    const newTreatments = [...(prev.treatments || [])];
                                                                    newTreatments.splice(idx, 1);
                                                                    return { ...prev, treatments: newTreatments };
                                                                });
                                                            }
                                                        }}
                                                        style={{ marginLeft: '8px', color: '#ef4444', cursor: 'pointer', fontSize: '10px' }}
                                                        title="Eliminar sesión"
                                                    >
                                                        <i className="bi bi-trash-fill"></i>
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" style={{ ...InnerLabelStyle, textAlign: 'center', borderBottom: '1px solid #000' }}>FECHA</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" style={{ verticalAlign: 'top', padding: '4px' }}>
                                                <input type="date" style={{ ...InputStyle, height: 'auto', fontSize: '10px', padding: '2px' }} value={treatment.date || ''} onChange={e => updateTreatment(idx, 'date', e.target.value)} disabled={isViewOnly} />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                            <td style={{ ...TdStyle, verticalAlign: 'top' }}>
                                <textarea
                                    style={{ ...InputStyle, minHeight: '80px', lineHeight: '1.5', backgroundImage: 'linear-gradient(transparent, transparent 23px, #ccc 24px)', backgroundSize: '100% 24px' }}
                                    value={treatment.diagnostics_complications || ''}
                                    onChange={e => updateTreatment(idx, 'diagnostics_complications', e.target.value)}
                                    disabled={isViewOnly}
                                    placeholder=""
                                />
                            </td>
                            <td style={{ ...TdStyle, verticalAlign: 'top' }}>
                                <textarea
                                    style={{ ...InputStyle, minHeight: '120px', lineHeight: '1.5', backgroundImage: 'linear-gradient(transparent, transparent 23px, #ccc 24px)', backgroundSize: '100% 24px' }}
                                    value={treatment.procedures || ''}
                                    onChange={e => updateTreatment(idx, 'procedures', e.target.value)}
                                    disabled={isViewOnly}
                                    placeholder=""
                                />
                            </td>
                            <td style={{ ...TdStyle, verticalAlign: 'top' }}>
                                <textarea
                                    style={{ ...InputStyle, minHeight: '80px', lineHeight: '1.5', backgroundImage: 'linear-gradient(transparent, transparent 23px, #ccc 24px)', backgroundSize: '100% 24px' }}
                                    value={treatment.prescriptions || ''}
                                    onChange={e => updateTreatment(idx, 'prescriptions', e.target.value)}
                                    disabled={isViewOnly}
                                    placeholder=""
                                />
                            </td>
                            <td style={{ ...TdStyle, verticalAlign: 'top' }}>
                                <table style={{ width: '100%', height: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ fontSize: '9px', fontWeight: 'bold', padding: '4px', borderBottom: '1px solid #000', borderRight: '1px solid #000', width: '30%', backgroundColor: '#f1f5f9', textAlign: 'center' }}>
                                                CÉDULA
                                            </td>
                                            <td style={{ padding: '0', borderBottom: '1px solid #000', width: '70%' }}>
                                                <input
                                                    type="text"
                                                    style={{ ...InputStyle, fontSize: '10px' }}
                                                    value={treatment.cedula !== undefined ? treatment.cedula : '0302804828'}
                                                    onChange={e => updateTreatment(idx, 'cedula', e.target.value)}
                                                    disabled={isViewOnly}
                                                    placeholder=""
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" style={{ height: '100%' }}>
                                                {/* Espacio en blanco para imprimir y sellar */}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    ))}
                    {!isViewOnly && (
                        <tr className="d-print-none">
                            <td colSpan="5" style={{ textAlign: 'center', padding: '10px', border: 'none' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData(prev => ({
                                            ...prev,
                                            treatments: [...(prev.treatments || []), { cedula: '0302804828' }]
                                        }));
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: '#e6e6e6',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: '#333'
                                    }}
                                >
                                    + Agregar nueva sesión
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>



            <div style={{ pageBreakBefore: 'always', marginTop: '40px' }} className="d-print-page-break">
                <table style={{ width: '100%', border: '3px solid #666', borderCollapse: 'collapse', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>
                    <tbody>
                        <tr>
                            <td colSpan="3" style={{ backgroundColor: '#c5c5fc', color: '#000', fontWeight: 'bold', padding: '4px 8px', borderBottom: '1px solid #000' }}>
                                A. DATOS DEL ESTABLECIMIENTO Y USUARIO
                            </td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#c1f0c1', textAlign: 'center', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', width: '33%' }}>INSTITUCIÓN DEL SISTEMA</td>
                            <td style={{ backgroundColor: '#c1f0c1', textAlign: 'center', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', width: '33%' }}>ESTABLECIMIENTO DE SALUD</td>
                            <td style={{ backgroundColor: '#c1f0c1', textAlign: 'center', padding: '4px', borderBottom: '1px solid #000', width: '34%' }}>NÚMERO DE HISTORIA CLÍNICA ÚNICA</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 'bold', textAlign: 'center', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>CONSULTORIO DENTAL</td>
                            <td style={{ fontWeight: 'bold', textAlign: 'center', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>LEMNARDENTAL</td>
                            <td style={{ borderBottom: '1px solid #000' }}><input type="text" style={{...InputStyle, textAlign: 'center'}} value={(consents[0] || {}).historia_clinica || ''} onChange={e => updateConsent(0, 'historia_clinica', e.target.value)} disabled={isViewOnly} /></td>
                        </tr>
                        <tr>
                            <td colSpan="3" style={{ padding: 0, borderBottom: '1px solid #000' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', borderRight: '1px solid #000', borderBottom: '1px solid #000', padding: '4px' }}>PRIMER APELLIDO</td>
                                            <td style={{ backgroundColor: '#c1f0c1', borderRight: '1px solid #000', borderBottom: '1px solid #000', padding: '4px' }}>SEGUNDO APELLIDO</td>
                                            <td style={{ backgroundColor: '#c1f0c1', borderRight: '1px solid #000', borderBottom: '1px solid #000', padding: '4px' }}>PRIMER NOMBRE</td>
                                            <td style={{ backgroundColor: '#c1f0c1', borderRight: '1px solid #000', borderBottom: '1px solid #000', padding: '4px' }}>SEGUNDO NOMBRE</td>
                                            <td style={{ backgroundColor: '#c1f0c1', borderRight: '1px solid #000', borderBottom: '1px solid #000', padding: '4px' }}>SEXO</td>
                                            <td style={{ backgroundColor: '#c1f0c1', borderBottom: '1px solid #000', padding: '4px' }}>EDAD</td>
                                        </tr>
                                        <tr>
                                            <td style={{ borderRight: '1px solid #000' }}><input type="text" style={{...InputStyle, textAlign: 'center', textTransform: 'uppercase'}} value={(consents[0] || {}).primer_apellido || ''} onChange={e => updateConsent(0, 'primer_apellido', e.target.value)} disabled={isViewOnly} /></td>
                                            <td style={{ borderRight: '1px solid #000' }}><input type="text" style={{...InputStyle, textAlign: 'center', textTransform: 'uppercase'}} value={(consents[0] || {}).segundo_apellido || ''} onChange={e => updateConsent(0, 'segundo_apellido', e.target.value)} disabled={isViewOnly} /></td>
                                            <td style={{ borderRight: '1px solid #000' }}><input type="text" style={{...InputStyle, textAlign: 'center', textTransform: 'uppercase'}} value={(consents[0] || {}).primer_nombre || ''} onChange={e => updateConsent(0, 'primer_nombre', e.target.value)} disabled={isViewOnly} /></td>
                                            <td style={{ borderRight: '1px solid #000' }}><input type="text" style={{...InputStyle, textAlign: 'center', textTransform: 'uppercase'}} value={(consents[0] || {}).segundo_nombre || ''} onChange={e => updateConsent(0, 'segundo_nombre', e.target.value)} disabled={isViewOnly} /></td>
                                            <td style={{ borderRight: '1px solid #000' }}><input type="text" style={{...InputStyle, textAlign: 'center'}} value={(consents[0] || {}).sexo || ''} onChange={e => updateConsent(0, 'sexo', e.target.value)} disabled={isViewOnly} /></td>
                                            <td><input type="text" style={{...InputStyle, textAlign: 'center'}} value={(consents[0] || {}).edad || ''} onChange={e => updateConsent(0, 'edad', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>            {consents.map((consent, idx) => (
                <div key={idx} style={{ marginTop: '20px', position: 'relative' }} className={idx > 0 ? "d-print-page-break" : ""}>
                    <table style={{ width: '100%', border: '3px solid #666', borderCollapse: 'collapse', marginBottom: '20px', fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>
                    <tbody>
                        <tr>
                            <td colSpan="4" style={{ backgroundColor: '#c5c5fc', color: '#000', fontWeight: 'bold', padding: '4px 8px', borderBottom: '1px solid #000' }}>
                                B. CONSENTIMIENTO INFORMADO {consents.length > 1 ? `(#${idx + 1})` : ''}
                                {consents.length > 1 && !isViewOnly && (
                                    <button 
                                        type="button" 
                                        onClick={() => handleDeleteConsent(idx)}
                                        style={{ float: 'right', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', padding: '2px 6px' }}
                                    >
                                        Eliminar
                                    </button>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#c1f0c1', width: '25%', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>CONSENTIMIENTO INFORMADO PARA:</td>
                            <td colSpan="3" style={{ borderBottom: '1px solid #000' }}><input type="text" style={{...InputStyle, textTransform: 'uppercase'}} value={consent.consentimiento_para || ''} onChange={e => updateConsent(idx, 'consentimiento_para', e.target.value)} disabled={isViewOnly} /></td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#c1f0c1', width: '10%', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>SERVICIO:</td>
                            <td style={{ width: '40%', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>
                                <input
                                    type="text"
                                    style={{...InputStyle, textTransform: 'uppercase'}}
                                    value={consent.servicio || 'ODONTOLOGÍA GENERAL'}
                                    onChange={e => updateConsent(idx, 'servicio', e.target.value)}
                                    disabled={isViewOnly}
                                />
                            </td>
                            <td style={{ backgroundColor: '#c1f0c1', width: '15%', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>TIPO DE ATENCIÓN:</td>
                            <td style={{ width: '35%', borderBottom: '1px solid #000', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '4px', textAlign: 'center' }}>AMBULATORIO <input type="checkbox" checked={consent.ambulatorio !== false} onChange={e => updateConsent(idx, 'ambulatorio', e.target.checked)} disabled={isViewOnly} /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>DIAGNÓSTICO:</td>
                            <td colSpan="2" style={{ borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>
                                <TermAutocompleteTextArea
                                    style={{ ...InputStyle, minHeight: '40px', resize: 'vertical' }}
                                    value={consent.diagnostico || ''}
                                    termsList={cieCodes}
                                    onUpdate={val => updateConsent(idx, 'diagnostico', val)}
                                    onSelectFull={item => {
                                        updateConsent(idx, 'diagnostico', item.description);
                                        updateConsent(idx, 'cie10', item.code);
                                    }}
                                    disabled={isViewOnly}
                                    insertField="description"
                                />
                            </td>
                            <td style={{ borderBottom: '1px solid #000', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', width: '30%', padding: '4px', borderRight: '1px solid #000' }}>CIE 10:</td>
                                            <td>
                                                <TermAutocompleteTextArea
                                                    style={{ ...InputStyle, minHeight: '40px', resize: 'vertical' }}
                                                    value={consent.cie10 || ''}
                                                    termsList={cieCodes}
                                                    onUpdate={val => updateConsent(idx, 'cie10', val)}
                                                    onSelectFull={item => {
                                                        updateConsent(idx, 'cie10', item.code);
                                                        updateConsent(idx, 'diagnostico', item.description);
                                                    }}
                                                    disabled={isViewOnly}
                                                    insertField="code"
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan="2" style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', width: '30%' }}>NOMBRE DEL PROCEDIMIENTO RECOMENDADO:</td>
                            <td colSpan="2" style={{ borderBottom: '1px solid #000' }}>
                                <TermAutocomplete
                                    style={InputStyle}
                                    value={consent.procedimiento_recomendado || ''}
                                    termsList={treatmentsCatalog}
                                    onUpdate={val => updateConsent(idx, 'procedimiento_recomendado', val)}
                                    disabled={isViewOnly}
                                    insertField="name"
                                    onSelectFullItem={(item) => {
                                        updateConsent(idx, 'procedimiento_recomendado', item.procedimiento_recomendado || item.name || '');
                                        updateConsent(idx, 'en_que_consiste', item.en_que_consiste || '');
                                        updateConsent(idx, 'como_se_realiza', item.como_se_realiza || '');
                                        if (item.beneficios) updateConsent(idx, 'beneficios', item.beneficios);
                                        if (item.riesgos_frecuentes) updateConsent(idx, 'riesgos_frecuentes', item.riesgos_frecuentes);
                                        if (item.riesgos_graves) updateConsent(idx, 'riesgos_graves', item.riesgos_graves);
                                        if (item.alternativas) updateConsent(idx, 'alternativas', item.alternativas);
                                        if (item.manejo_posterior) updateConsent(idx, 'manejo_posterior', item.manejo_posterior);
                                        if (item.consecuencias_no_realizar) updateConsent(idx, 'consecuencias_no_realizar', item.consecuencias_no_realizar);
                                        if (item.duracion) updateConsent(idx, 'duracion_intervencion', item.duracion);
                                    }}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>EN QUÉ CONSISTE:</td>
                            <td colSpan="3" style={{ borderBottom: '1px solid #000' }}>
                                <TermAutocomplete
                                    style={InputStyle}
                                    value={consent.en_que_consiste || ''}
                                    termsList={allTerms}
                                    onUpdate={val => updateConsent(idx, 'en_que_consiste', val)}
                                    disabled={isViewOnly}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000' }}>CÓMO SE REALIZA:</td>
                            <td colSpan="3" style={{ borderBottom: '1px solid #000' }}>
                                <TermAutocomplete
                                    style={InputStyle}
                                    value={consent.como_se_realiza || ''}
                                    termsList={allTerms}
                                    onUpdate={val => updateConsent(idx, 'como_se_realiza', val)}
                                    disabled={isViewOnly}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan="4" style={{ backgroundColor: '#c1f0c1', padding: '4px', borderBottom: '1px solid #000' }}>
                                GRÁFICO DE LA INTERVENCIÓN <span style={{ fontWeight: 'normal' }}>(incluya un gráfico previamente seleccionado que facilite la comprensión al paciente)</span>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan="4" style={{ height: 'auto', verticalAlign: 'top', padding: '10px' }}>
                                {!isViewOnly && (
                                    <div className="d-print-none" style={{ marginBottom: '15px' }}>
                                        <div style={{ maxWidth: '400px' }}>
                                            <TermAutocomplete
                                                style={{ border: '1px solid #ccc', borderRadius: '4px', width: '100%' }}
                                                value={consent._tempBuscadorImg || ''}
                                                termsList={consentGraphicsList.filter(g => g.is_active && !consent[`grafico_${g.id}`])}
                                                onUpdate={val => updateConsent(idx, '_tempBuscadorImg', val)}
                                                placeholder="Buscar y seleccionar un gráfico por nombre..."
                                                insertField="name"
                                                onSelectFullItem={(selected) => {
                                                    updateConsent(idx, `grafico_${selected.id}`, true);
                                                    updateConsent(idx, '_tempBuscadorImg', '');
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px' }}>
                                    {consentGraphicsList
                                        .filter(g => g.is_active && consent[`grafico_${g.id}`])
                                        .length > 0 ? consentGraphicsList
                                        .filter(g => g.is_active && consent[`grafico_${g.id}`])
                                        .map(grafico => (
                                        <div key={grafico.id} style={{ border: '2px solid #ccc', borderRadius: '8px', padding: '10px', textAlign: 'center', position: 'relative', minHeight: '150px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' }}>
                                            <h4 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: 'bold', padding: '0 24px' }}>{grafico.name}</h4>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100px' }}>
                                                <img src={grafico.image.startsWith('http') ? grafico.image : `${API_URL}${grafico.image}`} alt={grafico.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} width="150" height="100" />
                                            </div>
                                            {!isViewOnly && (
                                                <div 
                                                    className="d-print-none"
                                                    style={{ position: 'absolute', top: '10px', right: '10px', width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #ef4444', backgroundColor: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, color: 'white', fontWeight: 'bold', fontSize: '12px' }} 
                                                    onClick={() => updateConsent(idx, `grafico_${grafico.id}`, false)}
                                                    title="Quitar gráfico"
                                                >
                                                    X
                                                </div>
                                            )}
                                        </div>
                                    )) : (
                                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#64748b', padding: '20px' }}>
                                            {isViewOnly ? 'Ningún gráfico seleccionado para esta intervención.' : 'No hay gráficos seleccionados. Busque y añada uno usando el buscador de arriba.'}
                                        </div>
                                    )}
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', width: '30%', fontSize: '10px' }}>DURACIÓN ESTIMADA DE LA INTERVENCIÓN:</td>
                                            <td style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.duracion_intervencion || ''} onChange={e => updateConsent(idx, 'duracion_intervencion', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', fontSize: '10px' }}>BENEFICIOS DEL PROCEDIMIENTO:</td>
                                            <td style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.beneficios || ''} onChange={e => updateConsent(idx, 'beneficios', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', fontSize: '10px' }}>RIESGOS FRECUENTES (POCO GRAVES):</td>
                                            <td style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.riesgos_frecuentes || ''} onChange={e => updateConsent(idx, 'riesgos_frecuentes', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', fontSize: '10px' }}>RIESGOS POCO FRECUENTES (GRAVES):</td>
                                            <td style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.riesgos_graves || ''} onChange={e => updateConsent(idx, 'riesgos_graves', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" style={{ backgroundColor: '#c1f0c1', padding: '4px', borderBottom: '1px solid #000', fontSize: '10px' }}>
                                                DE EXISTIR, ESCRIBA LOS RIESGOS ESPECÍFICOS RELACIONADOS CON EL PACIENTE (edad, estado de salud, creencias, valores, etc):
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.riesgos_especificos_1 || ''} onChange={e => updateConsent(idx, 'riesgos_especificos_1', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.riesgos_especificos_2 || ''} onChange={e => updateConsent(idx, 'riesgos_especificos_2', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', fontSize: '10px' }}>ALTERNATIVAS AL PROCEDIMIENTO:</td>
                                            <td style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.alternativas || ''} onChange={e => updateConsent(idx, 'alternativas', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', borderBottom: '1px solid #000', fontSize: '10px' }}>DESCRIPCIÓN DEL MANEJO POSTERIOR AL PROCEDIMIENTO:</td>
                                            <td style={{ borderBottom: '1px solid #000' }}><input type="text" style={InputStyle} value={consent.manejo_posterior || ''} onChange={e => updateConsent(idx, 'manejo_posterior', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                        <tr>
                                            <td style={{ backgroundColor: '#c1f0c1', padding: '4px', borderRight: '1px solid #000', fontSize: '10px' }}>CONSECUENCIAS POSIBLES SI NO SE REALIZA EL PROCEDIMIENTO:</td>
                                            <td><input type="text" style={InputStyle} value={consent.consecuencias_no_realizar || ''} onChange={e => updateConsent(idx, 'consecuencias_no_realizar', e.target.value)} disabled={isViewOnly} /></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>


                
                </div>
            ))}

            
            {!isViewOnly && (
                <div style={{ textAlign: 'right', marginTop: '20px', position: 'relative' }} className="d-print-none">
                    <button
                        type="button"
                        onClick={() => setShowAddMenu(!showAddMenu)}
                        style={{ padding: '8px 16px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px' }}
                    >
                        +
                    </button>
                    {showAddMenu && (
                        <div style={{ position: 'absolute', right: 0, bottom: '100%', marginBottom: '5px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '150px', textAlign: 'left' }}>
                            <div 
                                style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee', fontWeight: 'bold' }}
                                onClick={handleAddAllConsents}
                            >
                                Todos
                            </div>
                            {(formData.diagnoses || []).filter(d => {
                                if (!d.cie) return false;
                                const currentCies = consents.map(c => c.cie10).filter(Boolean);
                                return !currentCies.includes(d.cie);
                            }).map((diag, i) => (
                                <div 
                                    key={i} 
                                    style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                    onClick={() => handleAddConsent(diag)}
                                >
                                    {diag.cie}
                                </div>
                            ))}
                            <div 
                                style={{ padding: '10px', cursor: 'pointer' }}
                                onClick={() => handleAddConsent(null)}
                            >
                                En blanco
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Fase12;
